/**
 * Creator Telegrams — skins (white-label themes per creator).
 *
 * A skin sets the colour variables (`--c-*` in src/styles/skins.css), the
 * display face and the animated background. It is chosen in
 * config.master.json → appearance.skin (synced to VITE_SKIN) and can be
 * previewed without redeploying with `?skin=<id>&bg=<background>`.
 */

export type SkinId = 'noir' | 'carbon' | 'studio' | 'studio-night' | 'blush' | 'arcade' | 'atelier'
export type BackgroundId = 'nebula' | 'aurora' | 'liquid' | 'grid' | 'silk' | 'blobs' | 'galaxy' | 'bokeh' | 'waves' | 'plain'
/** Constellation drawn over the background ('none' hides it, 'particles' keeps only the floating dust) */
export type ConstellationId = 'none' | 'particles' | 'virgo' | 'orion' | 'ursa' | 'cassiopeia' | 'scorpius' | 'leo' | 'crux'
type Rgb = [number, number, number]

export interface SkinBackground {
  /** Default animated background for the skin */
  mode: BackgroundId
  /** Ground colour of the shader */
  base: Rgb
  /** Mid tone (nebula dust, liquid blob, silk line) */
  mid: Rgb
  /** Brand glow */
  glow: Rgb
  /** Secondary accent glow */
  accent: Rgb
  /** How much brand colour bleeds into the background (0–1) */
  glowAmount: number
}

export interface Skin {
  id: SkinId
  name: string
  scheme: 'dark' | 'light'
  /** Telegram header / page background (hex) */
  headerColor: string
  background: SkinBackground
}

const hex = (h: string): Rgb => {
  const n = parseInt(h.replace('#', ''), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

export const SKINS: Record<SkinId, Skin> = {
  noir: {
    id: 'noir',
    name: 'Noir',
    scheme: 'dark',
    headerColor: '#020202',
    background: { mode: 'nebula', base: [0.001, 0.001, 0.002], mid: [0.013, 0.013, 0.018], glow: hex('#dc394b'), accent: hex('#c9a24a'), glowAmount: 0.1 },
  },
  carbon: {
    id: 'carbon',
    name: 'Carbon',
    scheme: 'dark',
    headerColor: '#0a0c0f',
    background: { mode: 'nebula', base: hex('#050608'), mid: hex('#12161c'), glow: hex('#3d8bff'), accent: hex('#8a96a8'), glowAmount: 0.22 },
  },
  'studio-night': {
    id: 'studio-night',
    name: 'Studio Night',
    scheme: 'dark',
    headerColor: '#12100e',
    background: { mode: 'aurora', base: hex('#0c0a09'), mid: hex('#1c1916'), glow: hex('#ff6a3d'), accent: hex('#d7f25c'), glowAmount: 0.55 },
  },
  arcade: {
    id: 'arcade',
    name: 'Arcade',
    scheme: 'dark',
    headerColor: '#0b0d12',
    background: { mode: 'grid', base: hex('#07080c'), mid: hex('#151923'), glow: hex('#b6ff3b'), accent: hex('#ff5fd9'), glowAmount: 0.6 },
  },
  studio: {
    id: 'studio',
    name: 'Studio',
    scheme: 'light',
    headerColor: '#f5f2ec',
    background: { mode: 'liquid', base: hex('#f5f2ec'), mid: hex('#ffe2d8'), glow: hex('#ffc2ac'), accent: hex('#eef7c4'), glowAmount: 0.8 },
  },
  blush: {
    id: 'blush',
    name: 'Blush',
    scheme: 'light',
    headerColor: '#fbf3ef',
    background: { mode: 'liquid', base: hex('#fbf3ef'), mid: hex('#fadbe5'), glow: hex('#f6c6d4'), accent: hex('#f8dccd'), glowAmount: 0.8 },
  },
  atelier: {
    id: 'atelier',
    name: 'Atelier',
    scheme: 'light',
    headerColor: '#efebe3',
    background: { mode: 'silk', base: hex('#efebe3'), mid: hex('#c9d6cd'), glow: hex('#1f4a3a'), accent: hex('#b08a3e'), glowAmount: 0.5 },
  },
}

export const BACKGROUNDS: BackgroundId[] = ['nebula', 'aurora', 'liquid', 'grid', 'silk', 'blobs', 'galaxy', 'bokeh', 'waves', 'plain']
export const CONSTELLATIONS: ConstellationId[] = ['none', 'particles', 'virgo', 'orion', 'ursa', 'cassiopeia', 'scorpius', 'leo', 'crux']

const isSkin = (v: unknown): v is SkinId => typeof v === 'string' && v in SKINS
const isBackground = (v: unknown): v is BackgroundId => typeof v === 'string' && (BACKGROUNDS as string[]).includes(v)
const isConstellation = (v: unknown): v is ConstellationId => typeof v === 'string' && (CONSTELLATIONS as string[]).includes(v)

export interface ResolvedAppearance {
  skin: Skin
  background: BackgroundId
  /** Skin palette with the optional color/color2 overrides applied */
  palette: SkinBackground
  /** Animation speed multiplier, 0 = static frame */
  motion: number
  /** Amount of shapes for particle/blob backgrounds (0.25–2) */
  density: number
  /** Constellation layer over the background */
  constellation: ConstellationId
  /** Background and constellation react to touch / cursor */
  interactive: boolean
}

const HEX = /^#?[0-9a-f]{6}$/i
const toHex = (v: string | null | undefined) => (v && HEX.test(v) ? (v.startsWith('#') ? v : `#${v}`) : undefined)
const clampNum = (raw: unknown, min: number, max: number, fallback: number) => {
  const n = Number(raw)
  return raw !== '' && raw != null && Number.isFinite(n) ? Math.min(Math.max(n, min), max) : fallback
}

/** Local overrides (demo customizer) that sit on top of the query string and the build config. */
export interface AppearanceOverrides {
  skin?: string
  background?: string
  color?: string
  color2?: string
  motion?: number
  density?: number
  /** Brand colour: used as the glow when no explicit background colour is set */
  brand?: string
  accent?: string
  /** Ground colour: recolours the shader's base and mid tones */
  bg?: string
  constellation?: string
  interactive?: boolean
}

/** Overrides (demo customizer) → query string (preview) → build-time config (VITE_*) → Noir. */
export function resolveAppearance(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  o: AppearanceOverrides = {},
): ResolvedAppearance {
  const params = new URLSearchParams(search)
  const env = import.meta.env
  const skinId = [o.skin, params.get('skin'), env.VITE_SKIN].find(isSkin) ?? 'noir'
  const skin = SKINS[skinId]
  // With a skin override the skin's own background wins over the build's (a different skin, a different mood)
  const bg = [o.background, ...(o.skin ? [] : [params.get('bg'), env.VITE_BACKGROUND])].find(isBackground) ?? skin.background.mode
  const motion = clampNum(o.motion ?? params.get('motion') ?? env.VITE_BG_MOTION, 0, 2, 1)
  const density = clampNum(o.density ?? params.get('density') ?? env.VITE_BG_DENSITY, 0.25, 2, 1)
  const constellation = [o.constellation, params.get('stars'), env.VITE_CONSTELLATION].find(isConstellation) ?? 'virgo'
  const interactive = o.interactive ?? (params.get('interactive') ?? env.VITE_BG_INTERACTIVE) !== 'false'
  // color / color2 recolour the background's two glow colours (e.g. Morphing Blobs)
  const buildColor = o.skin ? undefined : toHex(params.get('color')) ?? toHex(env.VITE_BG_COLOR)
  const buildColor2 = o.skin ? undefined : toHex(params.get('color2')) ?? toHex(env.VITE_BG_COLOR2)
  const color = toHex(o.color) ?? toHex(o.brand) ?? buildColor
  const color2 = toHex(o.color2) ?? toHex(o.accent) ?? buildColor2
  const ground = toHex(o.bg)
  const palette: SkinBackground = {
    ...skin.background,
    ...(ground ? groundTones(ground) : {}),
    glow: color ? hex(color) : skin.background.glow,
    accent: color2 ? hex(color2) : skin.background.accent,
  }
  return { skin, background: bg, palette, motion, density, constellation, interactive }
}

/** Shader base/mid tones derived from a custom ground colour. */
function groundTones(groundHex: string): { base: Rgb; mid: Rgb } {
  const base = hex(groundHex)
  const lum = 0.2126 * base[0] + 0.7152 * base[1] + 0.0722 * base[2]
  const dark = lum < 0.45
  const t = dark ? 0.08 : 0.07
  const mid: Rgb = [0, 1, 2].map((i) => base[i] + ((dark ? 1 : 0) - base[i]) * t) as Rgb
  return { base, mid }
}

/** Sets the skin on <html>: data-skin (colours/fonts), data-scheme/data-theme and the .dark/.light classes. */
export function applySkin(skin: Skin, root: HTMLElement = document.documentElement) {
  root.setAttribute('data-skin', skin.id)
  root.setAttribute('data-scheme', skin.scheme)
  root.setAttribute('data-theme', skin.scheme)
  root.classList.toggle('dark', skin.scheme === 'dark')
  root.classList.toggle('light', skin.scheme === 'light')
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', skin.headerColor)
}

/** Reads a `--c-*-rgb`-style triplet ("220 57 75") as "220, 57, 75" for canvas colours. */
export function cssRgb(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v ? v.split(/[\s,]+/).join(', ') : fallback
}
